import {
  GameType,
  TokenRedemptionOptionType,
} from '@daymade/common-lib/dbEnums';
import { TDrawTypeNumberSetup } from '@daymade/common-lib/dbJsonModels';
import clsx from 'clsx';
import YourNumbersPopup from 'components/enter/YourNumbersPopup';
import GenericButton from 'components/token-town/generic-button/GenericButton';
import PageSection from 'components/token-town/page-section/PageSection';
import PurchaseTokensMessage from 'components/token-town/purchase-tokens-message/PurchaseTokensMessage';
import QuantitySelector from 'components/token-town/select-entries/QuantitySelector';
import { useUserStore } from 'hooks/stores/useUserStore';
import useEntries from 'hooks/token-town/useEntries';
import { useWeeklyDrawPurchaseState } from 'hooks/token-town/useWeeklyDrawPurchaseState';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
import { tokenApiClient } from 'utils/apiClient';
import { ASSET_PREFIX } from 'utils/constants';
import HttpStatusCode from 'utils/HttpStatusCode';
import sectionStyles from '../../shared/Section.module.css';
import titleStyles from '../../shared/Title.module.css';
import NumberSelectionBox from '../number-selector/NumberSelectionBox';
import styles from './WeeklyDrawPurchase.module.css';

interface IWeeklyDrawPurchaseProps {
  redemptionOptionId: number;
  title: string;
  playerTokenCount: number;
  entryCost: number;
  numberRanges: TDrawTypeNumberSetup;
}

const WeeklyDrawPurchase = observer(
  ({
    redemptionOptionId,
    title,
    playerTokenCount,
    entryCost,
    numberRanges,
  }: IWeeklyDrawPurchaseProps) => {
    const router = useRouter();

    const { updateTokenBalance, fetchActiveTickets } = useUserStore();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { entries, addEntry, removeEntry, updateEntry } =
      useEntries(numberRanges);

    const [showChooseNumbersPopup, setShowChooseNumbersPopup] = useState(false);
    const openPopup = useCallback(() => setShowChooseNumbersPopup(true), []);
    const closePopup = useCallback(() => setShowChooseNumbersPopup(false), []);

    const { hasEnoughTokens, ctaText, ctaButtonDisabled, setInputError } =
      useWeeklyDrawPurchaseState({
        entryCost,
        playerTokenCount,
        numberRanges,
        entries,
      });

    const handleQuantityChange = (newQty: number) => {
      const diff = newQty - entries.length;
      if (diff > 0) {
        for (let i = 0; i < diff; i++) addEntry();
      } else if (diff < 0) {
        for (let i = 0; i < -diff; i++) removeEntry(entries.length - 1 - i);
      }
    };

    const handlePurchase = async () => {
      setError(null);
      setLoading(true);

      const result = await tokenApiClient.auth.redeemWithToken({
        params: {
          id: redemptionOptionId,
        },
        body: {
          [TokenRedemptionOptionType.ENTRY]: {
            entries: entries.map((entry) => ({
              type: GameType.LOTTERY,
              main: entry.main.filter((number) => number !== null),
              bonus: entry.bonus.filter((number) => number !== null),
            })),
          },
        },
      });

      switch (result.status) {
        case HttpStatusCode.OK:
          await updateTokenBalance();
          await fetchActiveTickets();
          router.push(`/token-town/confirmation?id=${result.body.id}`);
          return;
        case HttpStatusCode.BAD_REQUEST:
          setError(result.body.message);
          return;
        case HttpStatusCode.UNAUTHORIZED:
          setError(result.body.message);
          return;
        default:
          return;
      }
    };

    return (
      <PageSection>
        {showChooseNumbersPopup && (
          <YourNumbersPopup onClose={closePopup} entryMethod={title} />
        )}

        <div
          className={`${sectionStyles.sectionContainer} ${styles.morePadding} ${styles.pageContainer}`}
        >
          <p className={titleStyles.mainTitle}>{title}</p>

          <div
            className={`${sectionStyles.actionButtons}  ${styles.pageContainer}`}
          >
            <p className={`${titleStyles.infoDescription} ${styles.leftAlign}`}>
              1. Select your entries
            </p>
            <QuantitySelector
              type="entry"
              min={1}
              max={4}
              quantity={entries.length}
              setQuantity={handleQuantityChange}
              entryControlDisabled={false}
            />

            <div className={styles.titleContainer}>
              <p
                className={`${titleStyles.infoDescription} ${styles.leftAlign}`}
              >
                2. Select 5 lucky numbers
              </p>
              <img
                src={`${ASSET_PREFIX}/images/info-circle.svg`}
                className="hover:pointer-cursor h-5 ml-2 pt-1"
                onClick={openPopup}
                alt="more info"
              />
            </div>

            {entries.map((_entry, idx) => (
              <NumberSelectionBox
                key={idx}
                numberRanges={numberRanges}
                onEditNumbers={(newNums, inputError) => {
                  updateEntry(idx, newNums);
                  setInputError(inputError != undefined);
                }}
              />
            ))}

            <GenericButton
              loading={loading}
              onClick={() => handlePurchase()}
              disabled={ctaButtonDisabled}
            >
              {ctaText}
            </GenericButton>
          </div>

          {error && (
            <div
              className={clsx(titleStyles.infoDescription, styles.errorMessage)}
            >
              {error}
            </div>
          )}

          {!hasEnoughTokens && <PurchaseTokensMessage />}
        </div>
      </PageSection>
    );
  },
);

export default WeeklyDrawPurchase;
